import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_LOAN_ESTIMATION_TYPE } from '@/config/constants';
import {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
} from '@/apis/extended/userApi';
import { InvestorOnboardingModal } from '@/components/Onboarding/InvestorOnboardingModal';
import { PurchaseOnboardingModal } from '@/components/Onboarding/PurchaseOnboardingModal';
import { needsOnboarding } from '@/utils/rateWatchOnboarding';

const GOALS = [
  {
    id: 'purchase' as const,
    title: 'Buy a home',
    description:
      'Save listings, get loan estimates, and submit offers with guided deal-room tools.',
    icon: 'fa-light fa-house',
    to: '/properties',
  },
  {
    id: 'refinance' as const,
    title: 'Refinance my home',
    description:
      'Lower your rate or payment, or take cash out. Add your home and current mortgage, then apply.',
    icon: 'fa-light fa-arrows-rotate',
    to: '/properties?add=1&isRefinance=1',
  },
  {
    id: 'rate_watch' as const,
    title: 'Just watch refinance rates',
    description:
      'Compare market rates to your current mortgage on your home or investment — no application required.',
    icon: 'fa-light fa-chart-line',
    to: '/onboarding/rate-watch',
  },
  {
    id: 'investment' as const,
    title: 'Invest in property',
    description:
      'Track DSCR and investor programs for rental or portfolio acquisitions.',
    icon: 'fa-light fa-building',
    to: '/properties?filter=dscr',
  },
  {
    id: 'commercial' as const,
    title: 'Commercial property',
    description:
      'Get commercial loan estimates from a Personal Financial Statement instead of income qualification.',
    icon: 'fa-light fa-building-columns',
    to: '',
  },
];

export const OnboardingGoalPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: user, isLoading, refetch } = useGetCurrentUserQuery();
  const [updateUser, { isLoading: saving }] = useUpdateUserMutation();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [investorModalOpen, setInvestorModalOpen] = useState(false);
  const autoOpenedInvestorModal = useRef(false);

  useEffect(() => {
    if (!isLoading && user && !needsOnboarding(user)) {
      navigate('/properties', { replace: true });
    }
  }, [isLoading, user, navigate]);

  /** Admin-invited as commercial/investor: skip the goal picker and land straight in this modal. */
  useEffect(() => {
    if (
      !autoOpenedInvestorModal.current &&
      user &&
      needsOnboarding(user) &&
      user.loanEstimationType === USER_LOAN_ESTIMATION_TYPE.COMMERCIAL
    ) {
      autoOpenedInvestorModal.current = true;
      setInvestorModalOpen(true);
    }
  }, [user]);

  const finishPurchaseOnboarding = async () => {
    try {
      await updateUser({
        onboardingCompleted: true,
        onboardingIntent: 'purchase',
      }).unwrap();
      setPurchaseModalOpen(false);
      navigate('/properties');
    } catch {
      toast.error('Could not save your preference. Please try again.');
    }
  };

  const completeGoal = async (
    intent:
      | 'purchase'
      | 'refinance'
      | 'rate_watch'
      | 'investment'
      | 'commercial',
    path: string
  ) => {
    try {
      if (intent === 'refinance') {
        // Straight to adding the home + current mortgage — the refinance
        // estimate and application both start from it.
        await updateUser({
          onboardingCompleted: true,
          onboardingIntent: 'refinance',
        }).unwrap();
        // /properties bounces back here while the cached user still says
        // onboarding is incomplete — and would drop the add-home params.
        await refetch();
        navigate(path);
        return;
      }
      if (intent === 'rate_watch') {
        navigate(path);
        return;
      }
      if (intent === 'purchase') {
        // Straight to adding the home they're looking at; the extension is
        // offered there instead of as a separate welcome screen.
        await updateUser({
          onboardingCompleted: true,
          onboardingIntent: 'purchase',
        }).unwrap();
        await refetch();
        navigate('/properties?add=1');
        return;
      }
      if (intent === 'commercial') {
        setInvestorModalOpen(true);
        return;
      }
      await updateUser({
        onboardingCompleted: true,
        onboardingIntent: intent,
        loanEstimationType: USER_LOAN_ESTIMATION_TYPE.INVESTOR,
      }).unwrap();
      navigate(path);
    } catch {
      toast.error('Could not save your preference. Please try again.');
    }
  };

  if (isLoading || (user && !needsOnboarding(user))) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          What would you like to do first?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose a starting point — you can use other tools anytime from your
          portfolio.
        </Typography>
      </Box>

      <Stack spacing={2}>
        {GOALS.map((goal) => (
          <Card
            key={goal.id}
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: theme.palette.greyShades.border1,
              boxShadow: `0 4px 24px ${alpha(
                theme.palette.common.black,
                0.06
              )}`,
            }}
          >
            <CardActionArea
              disabled={saving}
              onClick={() => completeGoal(goal.id, goal.to)}
              sx={{ p: 0 }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                      flexShrink: 0,
                    }}
                  >
                    <i className={goal.icon} style={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {goal.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {goal.description}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          color="inherit"
          size="small"
          disabled={saving}
          onClick={finishPurchaseOnboarding}
        >
          Skip for now
        </Button>
      </Box>

      <PurchaseOnboardingModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        onComplete={finishPurchaseOnboarding}
      />
      <InvestorOnboardingModal
        open={investorModalOpen}
        onClose={() => setInvestorModalOpen(false)}
      />
    </Container>
  );
};
